import type { Beehiiv } from "@beehiiv/sdk"
import type { GetStaticProps } from "next"
import { type Metadata, PageMeta } from "@/components/common/Seo/PageMeta"
import {
  getPostBySlug,
  getPosts,
} from "@/components/resources/data/beehiiv/client"
import { toArticleHtml } from "@/components/resources/data/beehiiv/content"
import { SiteLayout } from "@/components/resources/layouts/site"
import { BeehiivArticlePage } from "@/components/resources/templates/BeehiivArticlePage"
import { shouldSkipStaticBuild } from "@/components/resources/utils/build"
import { BEEHIIV_PUBLICATION_ID } from "@/constants/env"

type PageProps = {
  post: Beehiiv.Post
  html: string
  meta: Metadata
}

const SinglePost = ({ post, html, meta }: PageProps) => (
  <>
    <PageMeta meta={meta} />
    <BeehiivArticlePage post={post} html={html} />
  </>
)

SinglePost.getLayout = SiteLayout

export default SinglePost

// "blocking" renders posts published after the build on the server too, never a loading shell
export async function getStaticPaths() {
  if (shouldSkipStaticBuild() || !BEEHIIV_PUBLICATION_ID) {
    return {
      paths: [],
      fallback: "blocking",
    }
  }

  try {
    const posts = await getPosts(BEEHIIV_PUBLICATION_ID)
    const now = Date.now() / 1000

    return {
      // "confirmed" includes scheduled posts, which must not be pre-rendered or reach the sitemap
      paths: posts.data
        .filter(({ publish_date }) => publish_date && publish_date <= now)
        .map(({ slug }) => ({ params: { slug } })),
      fallback: "blocking",
    }
  } catch (error) {
    console.error(
      "Failed to fetch newsletter posts for static paths, falling back to empty paths:",
      error
    )
    return {
      paths: [],
      fallback: "blocking",
    }
  }
}

export const getStaticProps: GetStaticProps<
  PageProps,
  { slug?: string }
> = async ({ params }) => {
  const { slug } = params ?? {}

  if (!slug) {
    throw new Error("Slug cannot be empty, check getStaticPaths")
  }

  if (!BEEHIIV_PUBLICATION_ID) {
    return { notFound: true }
  }

  const { data } = await getPostBySlug(BEEHIIV_PUBLICATION_ID, slug)

  if (!data) {
    // Beehiiv can return no post for a live one, e.g. while it is edited. The 404 must expire,
    // or it stays until the next deploy.
    return { notFound: true, revalidate: 30 }
  }

  // Ship the processed body only, not Beehiiv's whole web page
  const { content, ...post } = data

  return {
    props: {
      post,
      html: toArticleHtml(content.premium.web),
      meta: {
        title: post.title,
        description: post.meta_default_description,
        canonical: null,
        image: post.thumbnail_url ?? null,
        type: "article",
        url: null,
        noIndex: false,
      },
    },
    revalidate: 180, // TODO: temporary to allow content editing without an hour wait for build
  }
}
